import { selectUser } from '@/redux/auth/authSlice';
import { store } from '@/redux/store';
import toaster from '@/toaster';
import { omit, size } from 'lodash';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  invoiceForm,
  removeOpenedInvoice as removeOpenedInvoiceApi,
  sendInvoicePdf,
  sendInvoiceToKitchen,
  submitInvoice,
  updateInvoice,
} from '../../api/mutations/invoice';
import {
  addInvoice,
  removeTicket,
  selectInvoices,
  selectShift,
  setInvoices,
  setPending,
} from '../../redux/data/dataSlice';
import { InovicesType, InvoiceStatus, TicketType } from '../../redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getTicketTotal } from '../../utils/ticket';

export const useTicketSubmit = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const user = useAppSelector(selectUser);
  const currency = useAppSelector((s) => s.config.currency);
  const receipts = useAppSelector(selectInvoices);
  const shift = useAppSelector(selectShift);

  const submit = async (data: TicketType) => {
    setIsLoading(true);
    const payload: invoiceForm = {
      ID: data.ID,
      currency: currency ?? '',
      category: data.category ?? 'IN',
      items: data.items ?? [],
      type: data.type ?? 'PAY',
      payType: data.payType ?? 'CASH',
      discounts: data.discounts ?? [],
      shiftId: shift?.ID ?? '',
      posId: user?.devicePOS?.ID ?? '',
      storeId: user?.activeStore?._id,
      modifierOptions: data.modifierOptions ?? [],
      total: Number(getTicketTotal(data)),
      createdAt: data.createdAt,
      number: Number(`${(user?.devicePOS?.number ?? 1) * 100}` + `${size(receipts) + 1}`),
      customerId: data.customerId ?? null,
      status:
        data.category === 'DELIVERY'
          ? InvoiceStatus.OUT_FOR_DELIVERY
          : data.category === 'OUT'
            ? InvoiceStatus.PAID
            : InvoiceStatus.OPEN,
    };

    try {
      if (!data.savedToBackend) {
        await submitInvoice(payload);
      } else {
        const updatePayload = omit(payload, [
          'ID',
          'shiftId',
          'posId',
          'storeId',
          'number',
          'createdAt',
        ]);
        await updateInvoice(data.ID, updatePayload);
      }

      dispatch(removeTicket(data));
      dispatch(
        addInvoice({
          ...payload,
          shiftId: shift?.ID ?? '',
          pos: user?.devicePOS ?? null,
          store: user?.activeStore ?? null,
          creator: user ? { _id: user._id, name: user.name } : null,
        })
      );
      return true;
    } catch (err: any) {
      if (!err.statusCode) {
        // offline / network fail fallback
        dispatch(
          setPending({
            type: 'TICKET_SUBMIT',
            ID: data.ID,
            data: JSON.stringify(payload),
          })
        );
        dispatch(removeTicket(data));
        dispatch(
          addInvoice({
            ...payload,
            shiftId: shift?.ID ?? '',
            pos: user?.devicePOS ?? null,
            store: user?.activeStore ?? null,
            creator: user ? { _id: user._id, name: user.name } : null,
          })
        );
        return true;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const removeOpenedInvoice = async (ID: string) => {
    try {
      const invoices = store.getState().data.invoices;
      const invoice = invoices?.find((d) => d.ID === ID);
      if (!invoice) return;

      const newInvoices: InovicesType[] = (invoices || []).map((d) => {
        if (d.ID === ID) {
          return { ...d, status: InvoiceStatus.CANCELLED };
        }
        return d;
      });

      await removeOpenedInvoiceApi(ID);
      dispatch(setInvoices(newInvoices));
    } catch (err: any) {
      if (!err.statusCode) {
        toaster.error('Failed to remove opened invoice due to being offline');
      }
    }
  };

  const saveSale = async (ID: string, name?: string) => {
    const ticket = store.getState().data.tickets?.find((d) => d.ID === ID);
    if (!ticket) return;

    const payload: invoiceForm = {
      ID,
      currency: currency ?? '',
      category: ticket.category ?? 'IN',
      items: ticket.items ?? [],
      type: 'PAY',
      payType: 'CASH',
      discounts: ticket.discounts ?? [],
      shiftId: shift?.ID ?? '',
      posId: user?.devicePOS?.ID ?? '',
      storeId: user?.activeStore?._id ?? '',
      modifierOptions: ticket.modifierOptions ?? [],
      total: Number(getTicketTotal(ticket)),
      createdAt: ticket.createdAt,
      number: Number(`${(user?.devicePOS?.number ?? 1) * 100}` + `${size(receipts) + 1}`),
      customerId: ticket.customerId ?? null,
      status: InvoiceStatus.OPEN,
      tableId: ticket.tableId,
      name: name || ticket?.name || '',
    };

    try {
      setIsLoading(true);

      if (!ticket.savedToBackend) {
        await submitInvoice(payload);
      } else {
        const updatePayload = omit(payload, [
          'ID',
          'status',
          'shiftId',
          'posId',
          'storeId',
          'number',
          'createdAt',
          'status',
        ]);
        await updateInvoice(ID, updatePayload);
      }

      dispatch(removeTicket(ticket));
      dispatch(
        addInvoice({
          ...payload,
          shiftId: shift?.ID ?? '',
          pos: user?.devicePOS ?? null,
          store: user?.activeStore ?? null,
          creator: user ? { _id: user._id, name: user.name } : null,
        })
      );
    } catch (err: any) {
      if (!err.statusCode) {
        toaster.error('Failed to save sale due to being offline');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendToKitchen = async (ID: string) => {
    // first save the form to the backend
    const ticket = store.getState().data.tickets?.find((d) => d.ID === ID);

    if (!ticket) return;

    const payload: invoiceForm = {
      ID,
      currency: currency ?? '',
      category: ticket.category ?? 'IN',
      items: ticket.items ?? [],
      type: 'PAY',
      payType: 'CASH',
      discounts: ticket.discounts ?? [],
      shiftId: shift?.ID ?? '',
      posId: user?.devicePOS?.ID ?? '',
      storeId: user?.activeStore?._id ?? '',
      modifierOptions: ticket.modifierOptions ?? [],
      total: Number(getTicketTotal(ticket)),
      createdAt: ticket.createdAt,
      number: Number(`${(user?.devicePOS?.number ?? 1) * 100}` + `${size(receipts) + 1}`),
      customerId: ticket.customerId ?? null,
      status: InvoiceStatus.OPEN,
      tableId: ticket.tableId,
    };

    try {
      setIsLoading(true);

      if (!ticket.savedToBackend) {
        await submitInvoice(payload);
      } else {
        const updatePayload = omit(payload, [
          'ID',
          'status',
          'shiftId',
          'posId',
          'storeId',
          'number',
          'createdAt',
          'status',
        ]);
        await updateInvoice(ID, updatePayload);
      }

      await sendInvoiceToKitchen(payload.ID);
      payload.status = InvoiceStatus.SENT_TO_KITCHEN;

      setIsLoading(false);
      dispatch(removeTicket(ticket));
      dispatch(
        addInvoice({
          ...payload,
          shiftId: shift?.ID ?? '',
          pos: user?.devicePOS ?? null,
          store: user?.activeStore ?? null,
          creator: user ? { _id: user._id, name: user.name } : null,
        })
      );
    } catch (err: any) {
      if (!err.statusCode) {
        toaster.error('Failed to send invoice to kitchen due to being offline');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendInvoice = async (ID: string, email: string) => {
    const payload = { ID, email };
    try {
      await sendInvoicePdf(payload);
      return true;
    } catch (err: any) {
      if (!err.statusCode) {
        dispatch(
          setPending({
            type: 'INVOICE_SEND',
            ID: uuidv4(),
            data: JSON.stringify(payload),
          })
        );
        return true;
      }
    }
  };

  return { submit, sendInvoice, sendToKitchen, saveSale, isLoading, removeOpenedInvoice };
};
